import { useState, useRef, useEffect, useCallback } from "react";

const BARS = [
  0.4, 0.7, 1, 0.6, 0.85, 0.5, 0.9, 0.65, 1, 0.45, 0.75, 0.55, 0.8, 0.4, 0.6,
];

export default function VoiceMsgBubble({
  text,
  url,
  duration: durationProp,
}: {
  text: string;
  url?: string;
  duration?: number;  // 外部传入的可选值，优先使用
}) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // ★ 从 Audio 元素自动获取真实时长（解决 duration=0 不显示的问题）
  const [realDuration, setRealDuration] = useState(0);

  useEffect(() => {
    if (!url?.trim()) {
      audioRef.current?.pause();
      audioRef.current = null;
      setPlaying(false);
      return;
    }

    setPlaying(false);
    setRealDuration(0); // 重置时长
    const audio = new Audio(url);
    const onEnded = () => setPlaying(false);
    const onError = () => setPlaying(false);
    const onLoadedMetadata = () => {
      if (audio.duration && Number.isFinite(audio.duration)) {
        // 向上取整到秒，至少显示 1"
        const d = Math.ceil(audio.duration);
        setRealDuration(d > 0 ? d : 1);
      }
    };
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audioRef.current = audio;

    return () => {
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      if (audioRef.current === audio) audioRef.current = null;
    };
  }, [url]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !url?.trim()) return;

    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }

    try {
      if (audio.ended) audio.currentTime = 0;
      await audio.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  }, [playing, url]);

  return (
    <div className="flex flex-col items-end gap-1.5">
      {/* 语音块 */}
      <button
        type="button"
        onClick={togglePlay}
        disabled={!url?.trim()}
        className="flex items-center gap-2.5 px-4 py-3 rounded-[18px_4px_18px_18px] cursor-pointer active:scale-95 transition-all select-none disabled:opacity-45 disabled:cursor-not-allowed"
        style={{
          background: playing
            ? "linear-gradient(135deg, #00A043, #00B856)"
            : "linear-gradient(135deg, #00C851, #00A043)",
          minWidth: 140,
          maxWidth: 200,
        }}
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(255,255,255,0.25)" }}
        >
          <i
            className={playing ? "ri-pause-fill" : "ri-play-fill"}
            style={{ color: "#fff", fontSize: 16, marginLeft: playing ? 0 : 1 }}
          />
        </div>

        {/* 波形 */}
        <div
          className="flex items-center gap-[2px] flex-1"
          style={{ height: 28 }}
        >
          {BARS.map((v, i) => (
            <div
              key={i}
              className="rounded-full flex-shrink-0"
              style={{
                width: 2.5,
                height: v * 28,
                background: "rgba(255,255,255,0.75)",
                animation: playing
                  ? `wvBar 0.8s ease-in-out ${i * 0.055}s infinite alternate`
                  : "none",
                transition: "height 0.1s",
              }}
            />
          ))}
        </div>

        {/* 时长（优先使用从 Audio 元素获取的真实时长） */}
        <span
          className="text-xs font-semibold flex-shrink-0 whitespace-nowrap"
          style={{ color: "rgba(255,255,255,0.85)" }}
        >
          {realDuration || durationProp || 1}″
        </span>
      </button>

      {/* 识别文字 */}
      <div
        className="px-3 py-1.5 rounded-xl max-w-[220px]"
        style={{
          background: "#F5F6F8",
          border: "1px solid #EDEDED",
        }}
      >
        <div className="flex items-start gap-1.5">
          <i
            className="ri-translate-2 flex-shrink-0"
            style={{ color: "#999", fontSize: 11 }}
          />
          <p
            className="text-xs leading-relaxed"
            style={{ color: "#666" }}
          >
            {text}
          </p>
        </div>
      </div>
    </div>
  );
}
